import { FormEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import leftArrow from "../../assets/icons/left-arrow.svg";
import logoCobee from "../../assets/logo-cobee.svg";
import { Logout } from "../../components/Logout";
import Button from "../../components/button";
import { Footer } from "../../components/footer";
import { isAfter, isEqual } from "../../dates";
import { useMetrics } from "../../metrics";
import { RequestCardContext } from "../../requestCardProvider";
import { ROUTES } from "../../routes";
import { useRequestCard } from "../add-phone/useRequestCard";
import { useRequestVirtualCard } from "../add-phone/useRequestVirtualCard";
import classes from "./Birthdate.module.css";

const Birthdate = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { pageView, trackAction } = useMetrics();
  const { requestCard, isLoading } = useRequestCard();
  const { requestVirtualCard, isLoadingVirtualCard } = useRequestVirtualCard();
  const { t } = useTranslation();
  const { isFilledPhoneFormData, addressFormData, phoneFormData } =
    useContext(RequestCardContext);
  const [error, setError] = useState(false);

  const handleOnClickGoBack = () => {
    trackAction.click({
      screenName: "AddBirthdate",
      componentName: "button go back",
    });
  };

  const handleOnClickFinish = () => {
    trackAction.click({
      screenName: "AddBirthdate",
      componentName: "button finish",
    });
  };

  const handleForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { birthdate } = event.currentTarget
      .elements as typeof event.currentTarget.elements & {
      birthdate: HTMLInputElement;
    };

    const today = new Date();
    const sixteenYears = 16;
    const limitDate = new Date(
      today.getFullYear() - sixteenYears,
      today.getMonth(),
      today.getDate()
    );
    const formattedBirthdate = new Date(birthdate.value);
    const hasMoreThan16Years =
      isAfter(limitDate, formattedBirthdate) ||
      isEqual(limitDate, formattedBirthdate);
    if (!hasMoreThan16Years) {
      setError(true);
      return;
    }

    if (state?.virtualCard) {
      try {
        await requestVirtualCard({
          birthdate: birthdate.value,
          mobileCountryCode: phoneFormData.countryCode,
          mobilePhone: phoneFormData.phoneNumber,
        });
        navigate(ROUTES.HOME);
      } catch (error) {
        navigate(ROUTES.PAGE_FAILED, {
          state: {
            title: t("page-failed.request-card.title"),
            errorPage: "RequestCardFailed",
          },
        });
      }
      return;
    }

    const deliveryAddress = {
      birthdate: birthdate.value,
      mobileCountryCode: phoneFormData.countryCode,
      mobilePhone: phoneFormData.phoneNumber,
      street: `${addressFormData.address} ${addressFormData.addressNumber}${
        addressFormData.addressDetail
          ? `, ${addressFormData.addressDetail}`
          : ""
      }`,
      postalCode: addressFormData.postalCode,
      province: addressFormData.province,
      locality: addressFormData.city,
      streetName: addressFormData.address,
      streetNumber: addressFormData.addressNumber,
      details: addressFormData.addressDetail,
      ...(addressFormData.district && { district: addressFormData.district }),
    };

    try {
      await requestCard(deliveryAddress);
      navigate(ROUTES.HOME);
    } catch (error) {
      navigate(ROUTES.PAGE_FAILED, {
        state: {
          title: t("page-failed.request-card.title"),
          errorPage: "RequestCardFailed",
        },
      });
    }
  };

  useEffect(() => {
    pageView("Birthdate");
  }, [pageView]);

  useEffect(() => {
    if (isFilledPhoneFormData()) return;
    navigate(ROUTES.REQUEST_CARD);
  }, [isFilledPhoneFormData, navigate]);

  return (
    <div className={classes.container}>
      <header className="grid grid-cols-12 mt-4 h-16 items-center w-4/5">
        <Link
          className={`col-span-2 justify-end ${classes.goBack}`}
          to={ROUTES.ADD_PHONE}
          onClick={handleOnClickGoBack}
        >
          <img src={leftArrow} alt="" />
          {t("common.back")}
        </Link>
        <div className="flex justify-center col-start-3 col-span-8">
          <img
            className={`hidden md:flex  ${classes.logo}`}
            src={logoCobee}
            alt={t("common.logo")}
          />
        </div>
        <div className="col-span-1 items-start w-max">
          <Logout />
        </div>
      </header>
      <div className={classes.infoContainer}>
        <h1 className={classes.title}>
          {t("v2.requestCardFlow.birthdate.title")}
        </h1>
        <p className={classes.description}>
          {t("v2.requestCardFlow.birthdate.subtitle")}
        </p>
        <form className={classes.formContainer} onSubmit={handleForm}>
          <input
            onFocus={() => setError(false)}
            data-testid="birthdate-input"
            id="birthdate"
            type="date"
            className={classes.input}
            required={true}
          />
          {error && (
            <p className={classes.error}>
              {t("v2.requestCardFlow.birthdate.errors.has-more-sixteen-years")}
            </p>
          )}
          <div className={classes.buttonContainer}>
            <Button
              type="submit"
              disabled={isLoading || isLoadingVirtualCard}
              onClick={handleOnClickFinish}
              text={
                isLoading || isLoadingVirtualCard
                  ? t("v2.requestCardFlow.birthdate.button.loading")
                  : t("request-card.phone-number.button")
              }
            />
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export { Birthdate };
